import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { captureEvent } from "@Light/app/analytics";
import { Button } from "@Light/components/page/Button";
import { usePopover } from "@Light/components/page/popover/WithPopover";
import clsx from "clsx";
import { useState } from "react";
import { DayButton } from "./DayButton";
import { generateCalendarMonth, splitDate } from "./helpers";

type DateSelectorProps = {
  selectedDate: string;
  onChange: (v: string) => void;
  minSelectableDate: string;
  maxSelectableDate: string;
  disallowedDates: string[];
  isLoading?: boolean;
};

export const DateSelector: React.FC<DateSelectorProps> = ({
  selectedDate,
  onChange,
  minSelectableDate,
  maxSelectableDate,
  disallowedDates,
  isLoading,
}: DateSelectorProps) => {
  const [minYear, minMonth] = splitDate(minSelectableDate);

  const [year, setYear] = useState(minYear);
  const [month, setMonth] = useState(minMonth);
  const [tempSelectedDate, setTempSelectedDate] = useState(
    selectedDate || minSelectableDate,
  );
  const { close } = usePopover();

  const disallowedDatesSet = new Set(disallowedDates);

  const days = generateCalendarMonth(
    year,
    month,
    tempSelectedDate,
    minSelectableDate,
    maxSelectableDate,
    disallowedDatesSet,
  ).map((day) => (isLoading ? { ...day, isSelectable: false } : day));

  const todayInSelectedMonth = days.some(
    (day) => day.isToday && day.isCurrentMonth,
  );

  const monthName = new Date(year, month).toLocaleString("default", {
    month: "long",
  });

  const shortYear = String(year).slice(-2);

  const onPreviousMonth = () => {
    if (todayInSelectedMonth) {
      return;
    }
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
    captureEvent("enroll_select_date_prev_month");
  };

  const onNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
    captureEvent("enroll_select_date_next_month");
  };

  const onSelect = () => {
    onChange(tempSelectedDate);
    close();
    captureEvent("enroll_select_date_select");
  };

  return (
    <div>
      <div>
        <div className="flex flex-col mt-10 text-center gap-4">
          <div className="flex items-center text-gray-900">
            <button
              type="button"
              className={clsx(
                "-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500",
                todayInSelectedMonth && "opacity-0",
              )}
              disabled={todayInSelectedMonth}
              onClick={onPreviousMonth}
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>
            <div className="flex-auto text-sm font-semibold">
              {monthName} {"'"}
              {shortYear}
            </div>
            <button
              type="button"
              className="-m-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500"
              onClick={onNextMonth}
            >
              <span className="sr-only">Next month</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          <div className="flex flex-col">
            <div className="grid grid-cols-7 text-xs leading-6 text-gray-500">
              <div>S</div>
              <div>M</div>
              <div>T</div>
              <div>W</div>
              <div>T</div>
              <div>F</div>
              <div>S</div>
            </div>
            <div className="isolate grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow-sm ring-1 ring-gray-200">
              {days.map((day, dayIdx) => (
                <DayButton
                  key={day.date}
                  day={day}
                  dayIdx={dayIdx}
                  totalDays={days.length}
                  minSelectableDate={minSelectableDate}
                  maxSelectableDate={maxSelectableDate}
                  setTempSelectedDate={setTempSelectedDate}
                />
              ))}
            </div>
          </div>
          <Button className={clsx("w-full")} onClick={onSelect}>
            Select date
          </Button>
        </div>
      </div>
    </div>
  );
};
